import React from 'react';
import { LuSun, LuMoon, LuInfo } from 'react-icons/lu';

// The blurred routine behind the membership panel.
//
// ⚠ READ THIS BEFORE CHANGING IT: every shape here is SYNTHETIC. This renders
// geometry, never routine data.
//
// That is not a stylistic choice. `getRoutine` returns ENTITLEMENT_REQUIRED
// with no payload for a non-member (functions/routineFunctions/getRoutine.js),
// so on this surface there is no real routine in the browser to blur — and
// CLAUDE.md rule 1 forbids putting a client-side gate over a payload that
// carries the data. A CSS blur is a filter, not a gate: `filter: none` in
// devtools removes it. If you ever find yourself passing real `am`/`pm` items
// into this component, stop — you have turned an enforced paywall into a
// decorative one.
//
// ── WHY THERE IS STILL NO TEXT ──────────────────────────────────────────────
//
// The layout below mirrors the real page closely: stage rail, efficacy toggle,
// MORNING / EVENING / GUIDANCE groups, the same responsive card grid, product
// imagery, star rows, tag pills, strength ladders, gold shop buttons, and the
// standing disclaimer. What it does NOT carry is a single readable word.
//
// This used to be justified by the blur being heavy enough that nobody could
// read anything anyway. That argument is DEAD: the blur dropped from 6px to
// 3px and the panel's scrim from 0.8 to 0.5, because the old settings buried
// the regimen entirely. Type at 3px is partly legible, and a determined reader
// gets it exactly by removing one CSS filter.
//
// So the rule is now load-bearing rather than incidental. Invented product
// names here would be fabricated skincare recommendations, legible, in the DOM
// of a product that tells people what to put on their face. Bars carry the
// same shape and assert nothing. Same reasoning for the imagery: the bottles
// are CSS silhouettes, not photos of anything real.
//
// If the blur is ever lightened further, this gets MORE important, not less.
//
//   ┌──────────────────────────────────────────────────┐
//   │ ▁▁▁▁▁ ▓▓▓░░ ▁▁▁            ┌──────────┐          │ ← stage rail + toggle
//   │ ☀ MORNING ───────────────  └──────────┘          │
//   │ ┌────┐ ┌────┐ ┌────┐ ┌────┐                      │
//   │ │ ▟▙ │ │ ▟▙ │ │ ▟▙ │ │ ▟▙ │  ← product image     │
//   │ │▁▁▁ │ │▁▁▁ │ │▁▁▁ │ │▁▁▁ │  ← title / rating    │
//   │ │▪▪▪ │ │▪▪▪ │ │▪▪▪ │ │▪▪▪ │  ← tags + ladder     │
//   │ │▓▓▓▓│ │▓▓▓▓│ │▓▓▓▓│ │▓▓▓▓│  ← gold shop button  │
//   │ └────┘ └────┘ └────┘ └────┘                      │
//   │ ☾ EVENING ─────────────── (same grid)            │
//   │ ⓘ GUIDANCE ────────────── (advisory cards)       │
//   │ ▁▁▁▁▁ standing disclaimer ▁▁▁▁▁                  │
//   └──────────────────────────────────────────────────┘

// Tuned to survive the panel's scrim — this skeleton exists specifically to be
// seen THROUGH it, so it runs hotter than normal chrome.
const BAR = 'rounded-[3px] bg-white/[0.16]';
const DIM = 'rounded-[3px] bg-white/[0.10]';

// Mirrors Routine.js CARD_GRID exactly, so the blurred grid reflows at the
// same breakpoints the real one does.
const CARD_GRID =
  'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-5 mt-[18px]';

// A container silhouette in place of a product photo. Four shapes so a row
// does not read as the same object repeated. Pure CSS — no asset, and nothing
// depicting a real product.
function Bottle({ variant }) {
  const body = 'absolute bottom-0 rounded-[4px] bg-white/[0.13]';
  const cap = 'absolute rounded-[2px] bg-white/[0.18]';
  return (
    <div
      className="relative h-full w-full"
      style={{ background: 'linear-gradient(180deg, #3b3b39, #232322)' }}
      aria-hidden="true"
    >
      <div className="absolute inset-0 flex items-end justify-center pb-5">
        <div className="relative h-[62%] w-[34%]">
          {variant === 'tube' && (
            <>
              <div className={`${body} left-[18%] h-[86%] w-[64%] rounded-t-[10px]`} />
              <div className={`${cap} bottom-0 left-[30%] h-[9%] w-[40%]`} />
            </>
          )}
          {variant === 'dropper' && (
            <>
              <div className={`${body} left-[12%] h-[72%] w-[76%]`} />
              <div className={`${cap} left-[36%] top-0 h-[30%] w-[28%]`} />
            </>
          )}
          {variant === 'jar' && (
            <>
              <div className={`${body} left-0 h-[54%] w-full rounded-[6px]`} />
              <div className={`${cap} bottom-[50%] left-[6%] h-[16%] w-[88%]`} />
            </>
          )}
          {variant === 'pump' && (
            <>
              <div className={`${body} left-[16%] h-[70%] w-[68%]`} />
              <div className={`${cap} left-[40%] top-[4%] h-[26%] w-[20%]`} />
              <div className={`${cap} left-[28%] top-0 h-[7%] w-[44%]`} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Mirrors RoutineCard's ProductCard: image on top, then title, rating, the
// active line, tags, strength ladder, and the gold shop button pinned bottom.
function ProductSkeleton({ variant, tags = 3, pips = 4, climbed = 2, ladder = true }) {
  return (
    <div className="flex h-full flex-col overflow-hidden rounded-card border border-hairline bg-[#161615]">
      <div className="h-44 shrink-0 border-b border-hairline-soft">
        <Bottle variant={variant} />
      </div>

      <div className="flex flex-1 flex-col p-[18px]">
        {/* title — two lines, like a wrapped brand + product name */}
        <div className={`h-3 w-[92%] ${BAR}`} />
        <div className={`mt-2 h-3 w-[64%] ${BAR}`} />

        {/* rating row: gold star + count */}
        <div className="mt-3 flex items-center gap-1.5">
          <div className="h-2.5 w-2.5 rounded-[2px] bg-aurum/80" />
          <div className={`h-2 w-20 ${DIM}`} />
        </div>

        {/* the active line */}
        <div className={`mt-2.5 h-2.5 w-[74%] ${DIM}`} />

        {/* tag pills */}
        <div className="mt-3 flex flex-wrap gap-1.5">
          {Array.from({ length: tags }, (unused, i) => (
            <div
              key={i}
              className="h-[18px] rounded-full border border-hairline-soft bg-white/[0.05]"
              style={{ width: `${44 + ((i * 17) % 30)}px` }}
            />
          ))}
        </div>

        {/* strength rung + pip ladder */}
        {ladder && (
          <div className="mt-4">
            <div className="flex items-baseline justify-between">
              <div className="h-2.5 w-24 rounded-[3px] bg-aurum/45" />
              <div className={`h-2 w-14 ${DIM}`} />
            </div>
            <div className="mt-2 flex gap-1">
              {Array.from({ length: pips }, (unused, i) => (
                <div
                  key={i}
                  className="h-[5px] flex-1 rounded-[3px]"
                  style={{
                    background:
                      i < climbed
                        ? 'rgba(201,164,92,0.5)'
                        : i === climbed
                          ? '#E6C083'
                          : 'rgba(255,255,255,0.13)',
                  }}
                />
              ))}
            </div>
            <div className={`mt-2 h-2 w-[58%] ${DIM}`} />
          </div>
        )}

        {/* gold SHOP NOW button + adherence readout, pinned to the bottom so
            they align across a row exactly as the real cards do */}
        <div className="mt-auto pt-4">
          <div className="h-12 rounded-field border border-aurum/70 bg-aurum-faint" />
          <div className="mt-4 border-t border-hairline-soft pt-[15px]">
            <div className="flex items-center gap-2">
              <div className="h-1.5 w-1.5 rounded-full bg-aurum/70" />
              <div className={`h-2 w-24 ${DIM}`} />
            </div>
            <div className={`mt-1.5 h-2 w-[70%] ${DIM}`} />
          </div>
        </div>
      </div>
    </div>
  );
}

// Mirrors RoutineCard's AdvisoryCard — icon tile, title row, coaching lines.
function AdvisorySkeleton() {
  return (
    <div className="rounded-card border border-hairline-soft bg-[#161615] p-[18px]">
      <div className="flex items-center gap-[13px]">
        <div
          className="h-10 w-10 shrink-0 rounded-[9px] border border-hairline-soft"
          style={{ background: 'linear-gradient(180deg, #3b3b39, #232322)' }}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <div className={`h-3 w-[46%] ${BAR}`} />
            <div className={`h-2 w-20 ${DIM}`} />
          </div>
          <div className={`mt-2 h-2.5 w-[62%] ${DIM}`} />
        </div>
      </div>
      <div className={`mt-3.5 h-2.5 w-full ${DIM}`} />
      <div className={`mt-2 h-2.5 w-[88%] ${DIM}`} />
    </div>
  );
}

function GroupHeader({ icon }) {
  return (
    <div className="flex items-center gap-[11px] border-b border-hairline-soft pb-3.5">
      <span className="flex shrink-0 text-aurum" aria-hidden="true">{icon}</span>
      <div className={`h-2.5 w-[86px] ${BAR}`} />
    </div>
  );
}

const AM = [
  { variant: 'dropper', tags: 3, pips: 4, climbed: 1 },
  { variant: 'pump', tags: 2, pips: 5, climbed: 3 },
  { variant: 'jar', tags: 3, pips: 3, climbed: 2, ladder: false },
  { variant: 'tube', tags: 2, pips: 4, climbed: 2 },
];

const PM = [
  { variant: 'tube', tags: 3, pips: 5, climbed: 2 },
  { variant: 'dropper', tags: 2, pips: 4, climbed: 3 },
  { variant: 'pump', tags: 3, pips: 4, climbed: 1, ladder: false },
  { variant: 'jar', tags: 2, pips: 3, climbed: 1 },
];

export default function RoutineLockedPreview() {
  return (
    <div
      // aria-hidden + pointer-events-none: this is decoration behind a paywall
      // panel, and a screen reader announcing a wall of empty boxes would be
      // worse than silence. The panel carries the whole message.
      aria-hidden="true"
      className="pointer-events-none select-none blur-[3px] saturate-[0.9]"
      data-testid="routine-locked-preview"
    >
      {/* stage rail + the glowup CTA, mirroring the ready header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-wrap items-center gap-[18px]">
          <div className="flex items-center gap-[11px]">
            <div className={`h-2.5 w-[86px] ${BAR}`} />
            <div className="h-1 w-[150px] overflow-hidden rounded-full bg-white/[0.10]">
              <div
                className="h-full w-[38%] rounded-full"
                style={{ background: 'linear-gradient(90deg, #9A7B40, #E6C083)' }}
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className={`h-2.5 w-16 ${DIM}`} />
            <div className="h-2.5 w-12 rounded-[3px] bg-aurum/60" />
          </div>
        </div>

        {/* the IMAGINE MY GLOWUP button's silhouette — an Aurum-stroked pill */}
        <div className="h-12 w-[212px] shrink-0 rounded-field border border-aurum/70 bg-aurum/[0.06]" />
      </div>

      <div className="mt-8 lg:mt-[34px]">
        <GroupHeader icon={<LuSun size={17} strokeWidth={1.5} />} />
        <div className={CARD_GRID}>
          {AM.map((c, i) => <ProductSkeleton key={i} {...c} />)}
        </div>
      </div>

      <div className="mt-8 lg:mt-[34px]">
        <GroupHeader icon={<LuMoon size={17} strokeWidth={1.5} />} />
        <div className={CARD_GRID}>
          {PM.map((c, i) => <ProductSkeleton key={i} {...c} />)}
        </div>
      </div>

      <div className="mt-8 lg:mt-[34px]">
        <GroupHeader icon={<LuInfo size={17} strokeWidth={1.5} />} />
        <div className={CARD_GRID}>
          <AdvisorySkeleton />
          <AdvisorySkeleton />
        </div>
      </div>

      {/* standing disclaimer */}
      <div className="mt-[30px] flex items-start gap-[13px] rounded-field border border-hairline-soft bg-white/[0.03] px-[22px] py-[18px]">
        <div className="mt-px h-[18px] w-[18px] shrink-0 rounded-full bg-white/[0.12]" />
        <div className="min-w-0 flex-1">
          <div className={`h-2.5 w-full ${DIM}`} />
          <div className={`mt-2 h-2.5 w-[78%] ${DIM}`} />
        </div>
      </div>
    </div>
  );
}
